/**
 * COST PARITY: the backend's own fill arithmetic, in JavaScript.
 *
 * <h2>Why this file exists at all, when a second implementation is usually the defect</h2>
 *
 * This repository has twice paid for two implementations of one quantity — the Running Ledger
 * summing percentages while the Simulator compounded them, and a live HUD accumulating its own
 * equity beside a report that priced trades properly. The rule learned from both is that one
 * number gets one definition.
 *
 * <p>The discretionary book breaks that rule out of necessity, and the necessity is worth stating
 * precisely: a human trade is decided IN THE BROWSER, at a bar the backend has already finished
 * replaying. There is no round trip to price it through {@code AccountSimulation}, and asking for
 * one would put a network call between a key press and a fill.
 *
 * <p>So the arithmetic is mirrored, and the mirror is PINNED: {@code accountCosts.test.js} reads
 * {@code AccountSimulation.java} and {@code AccountConfig.java} off disk and asserts the five
 * expressions below still appear there verbatim. Java cannot hand a formula to JavaScript, so the
 * only alternative to a test is a comment nobody has to obey.
 *
 * <h2>The model, from `AccountSimulation.fill` and `AccountSimulation.size`</h2>
 *
 * <pre>
 *   sign          +1 for a long, -1 for a short
 *   slip          slippageBps / 10_000
 *
 *   entryFill     entryPrice * (1 + sign * slip)        adverse: a long buys higher
 *   exitFill      exitPrice  * (1 - sign * slip)        adverse: a long sells lower
 *
 *   commission    commissionPerShare * shares * 2       both legs
 *   slippageCost  (|entryFill-entry| + |exitFill-exit|) * shares
 *   gross         (exitFill - entryFill) * sign * shares
 *   net           gross - commission
 *
 *   riskPerShare  |entryFill - stopFill| + 2 * commissionPerShare
 *   byRisk        (equity * riskPct/100) / riskPerShare
 *   byBuyingPower (equity * maxLeverage) / entryFill
 *   shares        min(byRisk, byBuyingPower), floored unless fractions are allowed
 * </pre>
 *
 * <p><b>Slippage is applied PER LEG and direction-aware, never as one signed term.</b> A long buys
 * higher and sells lower; a short sells lower and buys back higher. One signed addition that is
 * right for a long PAYS a short — and a discretionary book is expected to carry shorts, so the
 * error would flatter exactly half of it.
 *
 * <p><b>Risk per share includes the cost of being stopped out.</b> The naive distance from entry to
 * stop does not deliver the budgeted loss: the entry fills worse, the stop fills worse, and two
 * commissions are paid. Omitting it oversizes by about 5% at the backend's own default levers,
 * which would make the human column's drawdown quietly larger than its risk setting claims.
 */

/** No sizing and no costs — the percentage model, and what an absent account means. */
export const FRICTIONLESS = Object.freeze({
  startingEquity: 0,
  riskPerTradePercent: 0,
  commissionPerShare: 0,
  slippageBps: 0,
  allowFractionalShares: false,
  maxLeverage: 1.0,
});

const num = (v, fallback = 0) => (Number.isFinite(v) ? v : fallback);

/**
 * Accepts the shape the dialog sends as well as the one the Java record uses.
 *
 * <p>The request field is `riskPerTradePct` and the Java component is `riskPerTradePercent`. Both
 * are read, because the account that prices the human book has to be the SAME OBJECT the replay
 * request was built from — rewriting its keys at the call site is one more place the two can
 * drift, and the symptom would be an unsized human column beside a sized algo one.
 */
export function normaliseAccount(config) {
  if (!config) return FRICTIONLESS;
  return {
    startingEquity: num(config.startingEquity),
    riskPerTradePercent: num(
      config.riskPerTradePercent !== undefined
        ? config.riskPerTradePercent : config.riskPerTradePct),
    commissionPerShare: num(config.commissionPerShare),
    slippageBps: num(config.slippageBps),
    allowFractionalShares: !!config.allowFractionalShares,
    maxLeverage: Number.isFinite(config.maxLeverage) && config.maxLeverage > 0
      ? config.maxLeverage : 1.0,
  };
}

/** +1 long, -1 short. Anything else is treated as a long, matching `Direction.sign()`. */
export const signOf = (direction) => (direction === 'SHORT' ? -1 : 1);

/** Slippage as a fraction of price, which is how every fill applies it. */
export const slippageFraction = (account) => num(account.slippageBps) / 10000;

/** `startingEquity > 0 && riskPerTradePercent > 0` — the Java predicate, not a guess at it. */
export const isSized = (account) =>
  account.startingEquity > 0 && account.riskPerTradePercent > 0;

export const hasFriction = (account) =>
  account.commissionPerShare > 0 || account.slippageBps > 0;

/** The price a position of this direction is OPENED at. Adverse. */
export function entryFillOf(entryPrice, direction, account) {
  return entryPrice * (1 + signOf(direction) * slippageFraction(account));
}

/** The price a position of this direction is CLOSED at. Adverse the other way. */
export function exitFillOf(exitPrice, direction, account) {
  return exitPrice * (1 - signOf(direction) * slippageFraction(account));
}

/**
 * How many shares this account would take, given an entry and its initial stop.
 *
 * <p>Mirrors `AccountSimulation.size` including the three refusals, each of which is a real
 * outcome rather than a guard against the impossible: no capital, a stop that gives no measurable
 * risk, and a budget that cannot afford one share.
 *
 * @returns {{shares:number, entryFill:number, capped:boolean, skipReason:string|null}}
 */
export function sizeShares({ direction, entryPrice, stop, account, equity }) {
  const acct = normaliseAccount(account);
  if (!Number.isFinite(entryPrice) || entryPrice <= 0) {
    return { shares: 0, entryFill: 0, capped: false, skipReason: 'no usable fill price' };
  }
  // AN UNSIZED ACCOUNT TAKES ONE SHARE, which is the frictionless percentage model — and is what
  // keeps a human book comparable with an unsized algo run rather than silently dollar-sized
  // beside a percentage one.
  if (!isSized(acct)) {
    return { shares: 1, entryFill: entryPrice, capped: false, skipReason: null };
  }
  if (!Number.isFinite(equity) || equity <= 0) {
    return { shares: 0, entryFill: 0, capped: false, skipReason: 'account was out of capital' };
  }

  const sign = signOf(direction);
  const slip = slippageFraction(acct);
  const entryFill = entryPrice * (1 + sign * slip);
  // The stop is an EXIT, so its slippage runs the other way.
  const stopFill = Number.isFinite(stop) ? stop * (1 - sign * slip) : NaN;

  if (!Number.isFinite(stopFill)) {
    // NO STOP WAS GIVEN, which is the ordinary case for a discretionary click. Risk cannot be
    // converted to a size at all, so the caller is told so rather than handed a size derived from
    // an adverse move nobody specified. `dualLedger` answers this by sizing on NOTIONAL and
    // recording that it did.
    return { shares: 0, entryFill, capped: false, skipReason: 'no stop, so risk is undefined' };
  }

  const riskPerShare = Math.abs(entryFill - stopFill) + 2 * acct.commissionPerShare;
  if (riskPerShare <= 0) {
    return { shares: 0, entryFill, capped: false, skipReason: 'the stop gave no measurable risk' };
  }

  const budget = equity * acct.riskPerTradePercent / 100;
  const byRisk = budget / riskPerShare;
  const byBuyingPower = equity * acct.maxLeverage / entryFill;
  const capped = byBuyingPower < byRisk;
  let shares = Math.min(byRisk, byBuyingPower);
  if (!acct.allowFractionalShares) shares = Math.floor(shares);
  if (!(shares > 0)) {
    return {
      shares: 0,
      entryFill,
      capped,
      skipReason: 'one share would have exceeded the risk budget or the buying power',
    };
  }
  return { shares, entryFill, capped, skipReason: null };
}

/**
 * Price a round trip at a share count the CALLER decided.
 *
 * <p>Mirrors `AccountSimulation.fillAt`/`fill`. The share count is NOT re-decided here — it is
 * already capped and already floored — for the same reason the Java method says so: the portfolio
 * layer and the live view both size by their own rules and must still fill by this one.
 *
 * @returns {{shares, entryFill, exitFill, commission, slippageCost, gross, net}}
 */
export function priceTrade({ direction, entryPrice, exitPrice, shares, account }) {
  const acct = normaliseAccount(account);
  const sign = signOf(direction);
  const slip = slippageFraction(acct);
  const entryFill = entryPrice * (1 + sign * slip);
  const exitFill = exitPrice * (1 - sign * slip);

  const commission = acct.commissionPerShare * shares * 2;
  const slippageCost = (Math.abs(entryFill - entryPrice)
    + Math.abs(exitFill - exitPrice)) * shares;
  const gross = (exitFill - entryFill) * sign * shares;

  return {
    shares,
    entryFill,
    exitFill,
    commission,
    slippageCost,
    gross,
    net: gross - commission,
  };
}
